/**
 * Files API Service
 * Handles all file-related API operations
 *
 * Base URL: /api/files (apiFetch adds /api prefix)
 */
import { useCallback, useMemo } from "react";
import { useApi } from "@/hooks/use-api";
import type {
    CanUploadResponseDto,
    FileContentResponseDto,
    FileListParams,
    FileListResponseDto,
    FileRawTextResponseDto,
    FileStatusDto,
    FileStatusUpdateDto,
    FileStorageResponseDto,
    FileStorageUpdateDto,
    GetFileStatusBatchResponseDto,
} from "@/types/api/files";
import type { JobResponseDto } from "@/types/api/jobs";

// Base URL for files endpoints (apiFetch adds /api prefix)
const FILES_BASE = "/files";

/**
 * Hook providing file API operations with authentication
 */
export function useFilesApi() {
    const api = useApi();

    /**
     * Upload a new file with page images.
     * POST /files (multipart/form-data)
     *
     * `organizationId` (sent when the uploader belongs to multiple orgs) is a form field
     * so the backend can scope the new FileStorage/Document to that organization.
     *
     * Optional `batch` triple correlates this upload with sibling uploads from the same
     * user click, so backend logs can be grouped/summarized per upload session.
     * Sent as headers (X-Batch-Id, X-File-Index, X-Batch-Total) — not form fields —
     * to keep multipart parsing untouched.
     *
     * Returns JobResponseDto with fileStorageId for polling.
     */
    const uploadFile = useCallback(
        async (
            file: File,
            images: File[],
            pageCount?: number,
            organizationId?: number,
            batch?: { batchId: string; fileIndex: number; totalFiles: number },
        ): Promise<JobResponseDto> => {
            const formData = new FormData();
            formData.append("file", file);

            // Append images with indexed names
            images.forEach((image, index) => {
                formData.append(`images[${index}]`, image);
            });

            if (pageCount !== undefined) {
                formData.append("pageCount", pageCount.toString());
            }

            if (organizationId !== undefined) {
                formData.append("organizationId", organizationId.toString());
            }

            const headers: Record<string, string> = {};
            if (batch) {
                headers["X-Batch-Id"] = batch.batchId;
                headers["X-File-Index"] = String(batch.fileIndex);
                headers["X-Batch-Total"] = String(batch.totalFiles);
            }

            return api.fetchWithAuth<JobResponseDto>(FILES_BASE, {
                method: "POST",
                body: formData,
                headers,
            });
        },
        [api],
    );

    /**
     * Get all files with pagination
     * GET /files?pageSize={pageSize}&skip={skip}
     */
    const getFiles = useCallback(
        async (params?: FileListParams): Promise<FileListResponseDto> => {
            const searchParams = new URLSearchParams();
            if (params?.pageSize) searchParams.set("pageSize", params.pageSize.toString());
            if (params?.skip) searchParams.set("skip", params.skip.toString());

            const query = searchParams.toString();
            const endpoint = query ? `${FILES_BASE}?${query}` : FILES_BASE;

            return api.get<FileListResponseDto>(endpoint);
        },
        [api],
    );

    /**
     * Get a single file by ID
     * GET /files/{id}
     */
    const getFileById = useCallback(
        async (id: number): Promise<FileStorageResponseDto> => {
            return api.get<FileStorageResponseDto>(`${FILES_BASE}/${id}`);
        },
        [api],
    );

    /**
     * Get file content with metadata
     * GET /files/{id}/content
     * Returns file content as base64 encoded string
     */
    const getFileContent = useCallback(
        async (id: number): Promise<FileContentResponseDto> => {
            return api.get<FileContentResponseDto>(`${FILES_BASE}/${id}/content`);
        },
        [api],
    );

    /**
     * Get browser-renderable preview bytes for formats the browser can't display
     * as stored (legacy .doc → server-converted .docx).
     * GET /files/{id}/preview-content
     * 400/422 for non-convertible files — callers fall back to download-only.
     */
    const getFilePreviewContent = useCallback(
        async (id: number): Promise<FileContentResponseDto> => {
            return api.get<FileContentResponseDto>(`${FILES_BASE}/${id}/preview-content`);
        },
        [api],
    );

    /**
     * Get file raw text (extracted/OCR text)
     * GET /files/{id}/rawtext
     */
    const getFileRawText = useCallback(
        async (id: number): Promise<FileRawTextResponseDto> => {
            return api.get<FileRawTextResponseDto>(`${FILES_BASE}/${id}/rawtext`);
        },
        [api],
    );

    /**
     * Update file properties
     * PATCH /files
     */
    const updateFile = useCallback(
        async (dto: FileStorageUpdateDto): Promise<void> => {
            await api.patch<void>(FILES_BASE, dto);
        },
        [api],
    );

    /**
     * Update file status
     * PATCH /files/status
     */
    const updateFileStatus = useCallback(
        async (dto: FileStatusUpdateDto): Promise<void> => {
            await api.patch<void>(`${FILES_BASE}/status`, dto);
        },
        [api],
    );

    /**
     * Get file status (lightweight polling endpoint)
     * GET /files/{id}/status
     */
    const getFileStatus = useCallback(
        async (id: number): Promise<FileStatusDto> => {
            return api.get<FileStatusDto>(`${FILES_BASE}/${id}/status`);
        },
        [api],
    );

    /**
     * Delete a file (soft delete)
     * DELETE /files/{id}
     * Sets status to Deleted and cancels pending jobs
     */
    const deleteFile = useCallback(
        async (id: number): Promise<void> => {
            await api.delete<void>(`${FILES_BASE}/${id}`);
        },
        [api],
    );

    /**
     * Get status of multiple files in a single batch request
     * POST /files/status/batch/get
     * Returns lightweight status DTOs with documents for polling
     */
    const getFileStatusBatch = useCallback(
        async (fileIds: number[]): Promise<GetFileStatusBatchResponseDto> => {
            return api.post<GetFileStatusBatchResponseDto>(`${FILES_BASE}/status/batch/get`, { fileIds });
        },
        [api],
    );

    /**
     * Check if the client can upload files (hasn't met quota)
     * GET /files/can-upload
     * Used by frontend to enable/disable upload functionality
     */
    const canUpload = useCallback(async (): Promise<CanUploadResponseDto> => {
        return api.get<CanUploadResponseDto>(`${FILES_BASE}/can-upload`);
    }, [api]);

    return useMemo(
        () => ({
            uploadFile,
            getFiles,
            getFileById,
            getFileContent,
            getFilePreviewContent,
            getFileRawText,
            updateFile,
            updateFileStatus,
            getFileStatus,
            deleteFile,
            getFileStatusBatch,
            canUpload,
        }),
        [
            uploadFile,
            getFiles,
            getFileById,
            getFileContent,
            getFilePreviewContent,
            getFileRawText,
            updateFile,
            updateFileStatus,
            getFileStatus,
            deleteFile,
            getFileStatusBatch,
            canUpload,
        ],
    );
}

/**
 * Type for the return value of useFilesApi
 */
export type FilesApi = ReturnType<typeof useFilesApi>;
